import React, {useCallback, useState} from 'react';

import {RequiredMark} from 'src/components/required-mark';
import {WidthItem} from 'src/components/width-item';

import Button from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Message} from '@pb/kit-component-message';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {useRemovePreset} from './useRemovePreset';


type Props = {
    presetId: string,
    presetName: string,
    onClose: () => void,
    onDone: () => void,
};

export const REMOVE_PRESET_MODAL_WIDTH = 600;

export const RemovePresetModal = ({presetId, presetName, onClose, onDone}: Props) => {
    const [reason, setReason] = useState('');
    const [error, setError] = useState<string | null>(null);

    const {event: action, busy} = useRemovePreset({
        presetId,
        onSuccess: onDone,
        onFail: setError,
    });

    const changeReason = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setReason(event.target.value);
    }, []);

    const blurReason = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        setReason(event.target.value.trim());
    }, []);

    const removePreset = useCallback(() => action(reason), [action, reason]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Delete Preset
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                {error ? <Message color="error">{error}</Message> : null}
                <Group direction="v">
                    <p>
                        Are you sure you want to delete the <b>{presetName}</b>?
                        If so, you will be taken back to the catalog page.
                        For further details, please refer to the <b>#ask-pb-entitlements</b> slack channel.
                    </p>
                    <WidthItem width="100%">
                        <Label><RequiredMark label="Reason"/></Label>
                        <Textarea
                            value={reason}
                            placeholder="Enter reason"
                            resizable={false}
                            onChange={changeReason}
                            onBlur={blurReason}
                            maxLength={128}
                        />
                    </WidthItem>
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button variant="Type.PRIMARY" busy={busy} onClick={removePreset} disabled={!reason}>
                        Delete
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
